<template>
  <div v-if="thought && topic" class="details media" :class="thoughtClass">
    <div v-if="showThumbnail && file && thumbnailPlacement == 'left'" class="media-left" :class="mediaThumbnailClass">
      <div class="float-start wdw-thought-thumbnail">
        <a :href="file.url" target="_blank">
          <img class="img-thumbnail" role="presentation" :src="thoughtThumbnail" />
        </a>
      </div>
    </div>
    <div :class="mediaBodyClass">
      <div :class="thoughtContentClass">
        <h3 class="thought-title">
          <span v-dompurify-html="thought.title"></span>
        </h3>
        <p v-if="thoughtLastUpdated">
          <i>
            <small
              v-dompurify-html="
                domainStore.translate('discuss.thought.field.last_update') +
                ': ' +
                formattedDate(UTCtoLocalTime(thoughtLastUpdated))
              "
            ></small>
          </i>
        </p>
        <div v-if="!detailedAttributes" class="attribute-list">
          <thought-attribute-tags :attributes="attributesToDisplay" />
        </div>

        <!-- Themencode -->
        <div
          v-if="project.config && project.config.show_thought_themenbereich_icon"
          class="d-flex align-items-center mb-2"
        >
          <div v-for="value in themeCode.value_all" v-bind:key="value" class="p-1">
            <img
              :src="themeUrl + `/${value}.png`"
              class="img-fluid"
              style="width: 35px; height: 35px; min-width: 35px"
              alt=""
              @click="setFilterFromThought('attribute' + themeCode.id, value)"
            />
          </div>
        </div>

        <!-- Beteiligungsstufen -->
        <div class="mb-2" v-if="stufeCode && toBool(project.config && project.config.show_thought_stufe_icon)">
          <img
            v-for="(value, index) in stufeCode.value_all"
            v-bind:key="value"
            class="img-fluid mx-1"
            :title="stufeCode.value_human_all[index]"
            :src="stufeUrl + `/${value}.png`"
            style="width: 35px; height: 35px"
            :alt="'stufe' + stufeCode.value_human_all[index]"
          />
        </div>

        <p class="thought-description">
          <span style="white-space: pre-wrap; word-break: break-word" v-dompurify-html="thought.content"></span>
        </p>
        <div v-if="detailedAttributes" class="attribute-list">
          <attribute-show-list :attributes="attributesToDisplay" />
        </div>
      </div>

      <div v-if="showThumbnail && file && thumbnailPlacement == 'right'" class="my-3" :style="mediaThumbnailRightStyle">
        <div :class="'float-end wdw-thought-thumbnail'">
          <a :href="file.url" target="_blank">
            <img class="img-thumbnail" role="presentation" :src="thoughtThumbnail" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toRefs, computed, onMounted } from 'vue';
import { projectStore } from '@/store/project-store';
import { domainStore } from '@/store/domain-store';
import { filterStore } from '@/store/filter-store';

import ThoughtAttributeTags from './AttributeTags.vue';
import AttributeShowList from '@/components/attribute/ShowList.vue';

import useConverter from '@/composables/general/useConverter';
import { useRoute } from 'vue-router';

// TODO -> fully implement

export default {
  name: 'ThoughtContent',
  props: {
    thought: {
      type: Object,
      required: true,
    },
    topic: {
      type: Object,
      required: true,
    },
  },

  setup(props, context) {
    const { toBool } = useConverter();
    const { thought, topic } = toRefs(props);
    const { formattedDate, UTCtoLocalTime } = useConverter();
    const route = useRoute();
    const thoughtClass = computed(() => {
      return 'thought-' + thought.value.id;
    });

    const project = computed(() => {
      return projectStore.getState().project;
    });

    const themeUrl = computed(() => {
      if (project.value.config) {
        return project.value.config.thought_themenbereich_icon_url;
      }
      return '';
    });
    const projectThemeAtrribute = computed(() => {
      if (project.value.config) {
        return project.value.config.themenbereich_attribute;
      }
      return '';
    });
    const projectStufeAtrribute = computed(() => {
      if (project.value.config && project.value.config.stufe_attribute) {
        return project.value.config.stufe_attribute;
      }
      return '';
    });
    const stufeCode = computed(() => {
      return props.thought.attributes.find(element => {
        return element.code == projectStufeAtrribute.value;
      });
    });
    const stufeUrl = computed(() => {
      return project.value.config.thought_stufe_icon_url;
    });
    const themeCode = computed(() => {
      return props.thought.attributes.find(element => {
        return element.code == projectThemeAtrribute.value;
      });
    });

    const thoughtLastUpdated = computed(() => {
      return project.value.config?.show_thought_last_updated ? thought.value.last_update : false;
    });

    const showThumbnail = computed(() => {
      if (project.value.config?.thought_image_preview) {
        return project.value.config.thought_image_preview.includes('detail');
      }
      return true;
    });
    const mediaThumbnailClass = computed(() => {
      if (project.value.config?.thought_image_preview_width) {
        const size = project.value.config.thought_image_preview_width;
        let cssClass = 'media-width-128';
        if (size == 'w256' || size == 'sq256') {
          cssClass = 'media-width-256';
        } else if (size == 'w400') {
          cssClass = 'media-width-400';
        }
        return cssClass;
      }
      return { 'media-width-256': true };
    });
    const detailedAttributes = computed(() => {
      return project.value.config?.attribute_style == 'in_detail' ? true : false;
    });

    const attributesToDisplay = computed(() => {
      const attributeList = [];
      topic.value.attributes.forEach(topic_attr => {
        thought.value.attributes.forEach(attr_val => {
          if (
            topic_attr.id === attr_val.id &&
            (topic_attr.relation_data.show_frontend_tag == 1 || detailedAttributes.value)
          ) {
            attributeList.push(attr_val);
          }
        });
      });
      return attributeList;
    });

    const thumbnailPlacement = computed(() => {
      return project.value.config?.thought_detailview_image_position || 'left';
    });

    const file = computed(() => {
      let fileWithThumb = null;
      for (const file of thought.value.files) {
        if (file.thumbnails && file.thumbnails.sq256) {
          fileWithThumb = file;
          break;
        }
      }
      return fileWithThumb;
      // Zum Testen auf lokalen System wo Thumbnail nicht automatisch generiert wird
      /*
      const fileWithThumb = thought.value.files.find(x => x.type == 'file');
      return fileWithThumb ? fileWithThumb : undefined;
      */
    });

    const thoughtThumbnail = computed(() => {
      if (project.value.config?.thought_image_preview_width) {
        const size = project.value.config.thought_image_preview_width;
        const thumbUrl = file.value.thumbnails[size];
        // w256 is always the last choice
        return thumbUrl || file.value.thumbnails.w256;
      } else {
        return file.value.thumbnails.w256;
      }
      // Zum Testen auf lokalen System wo Thumbnail nicht automatisch generiert wird
      // return file.value ? file.value.url : '';
    });

    const mediaBodyClass = computed(() => {
      return {
        'media-body': true,
        'd-flex': thumbnailPlacement.value == 'right' && showThumbnail.value && file.value,
      };
    });
    const thoughtContentClass = computed(() => {
      return {
        'thought-content': true,
      };
    });
    const mediaThumbnailRightStyle = computed(() => {
      // For right-side thumbnail, we use the image width as flex-width property, not mediaThumbnailClass
      const imgWidth = project.value.config?.thought_image_preview_width || 'w256';
      let flexWidth = '256px;';
      if (imgWidth == 'w128' || imgWidth == 'sq128') {
        flexWidth = '128px';
      } else if (imgWidth == 'w400') {
        flexWidth = '400px';
      }
      return 'flex: 0 0 ' + flexWidth + ';';
    });

    // Sets key-value that is passed as filter to the filter form
    // Note: This is done through change in hash parameters
    function setFilterFromThought(key, value) {
      filterStore.setIndividualFilter({ key: key, value: value }, route, true);
    }

    return {
      domainStore,
      stufeCode,
      stufeUrl,
      mediaThumbnailClass,
      mediaBodyClass,
      thoughtContentClass,
      thoughtClass,
      mediaThumbnailRightStyle,
      thoughtLastUpdated,
      attributesToDisplay,
      themeCode,
      themeUrl,
      file,
      project,
      showThumbnail,
      detailedAttributes,
      thoughtThumbnail,
      thumbnailPlacement,
      formattedDate,
      setFilterFromThought,
      UTCtoLocalTime,
      toBool,
    };
  },

  components: {
    ThoughtAttributeTags,
    AttributeShowList,
  },
};
</script>

<style scoped lang="scss">
h3 {
  /* to be able to float text next to image, required for overruling customer styles */
  clear: none;
  &.thought-title {
    font-size: 1.25rem;
  }
}
</style>
